/**
 * Unit Classification for Field Type Selection
 *
 * Leverages the same logic as UnitConversionService to determine
 * which units can be converted vs which are universal/scientific.
 * This creates a single source of truth for unit behavior.
 */

/**
 * Check if a unit can be converted (has metric/imperial equivalents)
 * Uses the same logic as UnitConversionService.canUnitBeConverted()
 * @param unit - The unit string to check
 * @returns true if the unit has conversion capabilities
 */
const canUnitBeConverted = (unit: string | undefined): boolean => {
	if (!unit) {
		return false;
	}

	const normalizedUnit = unit.trim();

	// Same conversion maps as UnitConversionService
	const lengthConversions: Record<string, number> = {
		mm: 0.001, // to meters
		m: 1, // base unit
		mH2O: 1, // same as meters for pressure head
		mAD: 1, // same as meters for elevation
	};

	const flowConversions: Record<string, number> = {
		'm³/s': 1, // base unit
		'l/s': 0.001, // liters per second to cubic meters per second
	};

	// Check if the unit exists in any conversion category
	return (
		lengthConversions[normalizedUnit] !== undefined ||
		flowConversions[normalizedUnit] !== undefined
	);
};

/**
 * Currency Units - handled by CurrencyField
 * These have special formatting requirements
 */
const CURRENCY_UNITS = ['$'];

export enum FieldType {
	SYSTEM_OF_MEASUREMENT = 'system-of-measurement', // Use UnitField
	SCIENTIFIC_UNIT = 'scientific-unit', // Use ScientificUnitField
	CURRENCY = 'currency', // Use CurrencyField
	UNKNOWN = 'unknown', // Fallback
}

/**
 * Classify a unit string to determine which field component to use
 * Uses the same logic as UnitConversionService for consistent behavior
 */
const classifyUnit = (unit: string | undefined): FieldType => {
	if (!unit?.trim()) return FieldType.UNKNOWN;

	const normalizedUnit = unit.trim();

	// Check currency first (special formatting needs)
	if (CURRENCY_UNITS.includes(normalizedUnit)) {
		return FieldType.CURRENCY;
	}

	// Use conversion service logic to determine field type
	if (canUnitBeConverted(unit)) {
		return FieldType.SYSTEM_OF_MEASUREMENT; // → UnitField (with conversions)
	} else {
		return FieldType.SCIENTIFIC_UNIT; // → ScientificUnitField (no conversions)
	}
};

/**
 * Check if a unit requires system of measurement field (current UnitField)
 */
export const isSystemOfMeasurementUnit = (
	unit: string | undefined,
): boolean => {
	return classifyUnit(unit) === FieldType.SYSTEM_OF_MEASUREMENT;
};

/**
 * Check if a unit requires scientific unit field
 */
export const isScientificUnit = (unit: string | undefined): boolean => {
	return classifyUnit(unit) === FieldType.SCIENTIFIC_UNIT;
};

/**
 * Check if a unit requires currency field
 */
export const isCurrencyUnit = (unit: string | undefined): boolean => {
	return classifyUnit(unit) === FieldType.CURRENCY;
};
